/**
 * SOLID Review
 * S — Strong. One concern: turn the room roster plus the current round state into the rows
 *     TopSection renders. No I/O, no store access, no React — every input is a parameter.
 * O — Good. A new badge or label is a new branch in one of the two small helpers below rather
 *     than another clause in an 85-line inline IIFE.
 * L — N/A — no inheritance hierarchy.
 * I — Good. One function, one input record; the caller passes only what it already has.
 * D — Strong. Depends on IServicePlayerDisplayInfo (the type it produces, defined beside it) and the
 *     GamePhase enum. Nothing outward.
 */
import { GamePhase } from '../../../../application/ports/ClientVocabulary';
import { fmtScore } from '../../../utils/fmtScore';
import type { IServicePlayerDisplayInfo } from './types';
import type { IEntityLiveKitParticipant } from '../../../../application/ports/IGatewayLiveKit';

const DEFAULT_LIVES = 6;

export interface IServiceTopPlayersInput {
  readonly roomPlayers: ReadonlyArray<{ id: string; displayName: string }>;
  readonly localUser: { playerId: string; displayName: string; matchCredits?: number } | null;
  readonly playerLives: Readonly<Record<string, number>>;
  readonly suspendedPlayerIds: readonly string[];
  readonly activeRollerId: string | null;
  readonly challengerId: string | null;
  readonly phase: GamePhase;
  readonly lastClaimedScore: number | null;
  readonly claimWasBlind: boolean;
  readonly decisionCountdown: { challengerId: string; secondsRemaining: number } | null;
  readonly participants: ReadonlyMap<string, IEntityLiveKitParticipant>;
  /** Offered on the placeholder row when there is no identity yet, so a tap can create one. */
  readonly onPressLocalPlaceholder: () => void;
}

/** The label above a tile: a countdown beats a pending claim, and most of the time there is neither. */
function hudLabelFor(id: string, i: IServiceTopPlayersInput): string | undefined {
  if (i.decisionCountdown !== null && i.decisionCountdown.challengerId === id) {
    return `Auto-believe in ${i.decisionCountdown.secondsRemaining}s`;
  }
  if (i.phase === GamePhase.DECIDING && i.lastClaimedScore !== null && id === i.challengerId) {
    return `${i.claimWasBlind ? 'CLAIMED BLIND' : 'Claimed'}: ${fmtScore(i.lastClaimedScore)}`;
  }
  return undefined;
}

/**
 * The badge on a tile.
 *
 * SUSPENDED wins over everything, deliberately: a disconnected player who is also the roller
 * must not read as "AWAITING DECISION", because the table is waiting on their CONNECTION, not
 * on a choice they are about to make.
 */
function statusBadgeFor(id: string, i: IServiceTopPlayersInput): string | undefined {
  if (i.suspendedPlayerIds.includes(id)) return 'SUSPENDED';
  if (i.phase !== GamePhase.DECIDING) return undefined;
  if (id === i.activeRollerId) return 'AWAITING DECISION';
  if (id === i.challengerId) return 'DECIDE';
  return undefined;
}

/** The local player's own row, used both as a placeholder and when they are not yet seated. */
function localRow(i: IServiceTopPlayersInput): IServicePlayerDisplayInfo {
  const { localUser } = i;
  return {
    id: localUser?.playerId ?? 'local-placeholder',
    displayName: localUser?.displayName ?? 'You',
    isLocal: true,
    lives: localUser ? (i.playerLives[localUser.playerId] ?? DEFAULT_LIVES) : DEFAULT_LIVES,
    maxLives: DEFAULT_LIVES,
    isActiveTurn: false,
    participant: localUser ? (i.participants.get(localUser.playerId) ?? null) : null,
    matchCredits: localUser?.matchCredits ?? 0,
    onPress: !localUser ? i.onPressLocalPlaceholder : undefined,
  };
}

/**
 * Build the TopSection roster.
 *
 * Lifted out of AppRoot on 2026-08-16, where it was an 85-line inline IIFE inside a file
 * excluded from the coverage denominator — so none of the badge/label precedence below was
 * measured, and it is precisely the sort of nested-ternary logic that reads as obvious and
 * is not.
 *
 * The local player is always present: seated players come from the room roster, and if the
 * local player is not among them (a spectator, or a roster that has not synced yet) their row
 * is unshifted at the front rather than omitted — a player who cannot see themselves has no
 * lives display and no credits.
 */
export function buildTopPlayers(input: IServiceTopPlayersInput): IServicePlayerDisplayInfo[] {
  if (input.roomPlayers.length === 0) return [localRow(input)];

  const rows = input.roomPlayers.map((p): IServicePlayerDisplayInfo => ({
    id: p.id,
    displayName: p.displayName,
    isLocal: p.id === input.localUser?.playerId,
    lives: input.playerLives[p.id] ?? DEFAULT_LIVES,
    maxLives: DEFAULT_LIVES,
    isActiveTurn: p.id === input.activeRollerId,
    participant: input.participants.get(p.id) ?? null,
    matchCredits: p.id === input.localUser?.playerId ? (input.localUser?.matchCredits ?? 0) : undefined,
    hudLabel: hudLabelFor(p.id, input),
    statusBadge: statusBadgeFor(p.id, input),
    onPress: undefined,
  }));

  const localIsSeated = input.roomPlayers.some((p) => p.id === input.localUser?.playerId);
  if (!localIsSeated) rows.unshift(localRow(input));
  return rows;
}
